import { ArrowRight, Clock } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { APPLY_URL, PAYMENT_URL } from "@/lib/constants";

export function Booking() {
  return (
    <section
      id="book"
      data-testid="booking-section"
      className="border-t border-black/10"
    >
      <div className="mx-auto max-w-[1600px] px-5 py-32 md:px-10 md:py-48">
        <div className="grid gap-16 lg:grid-cols-2">
          <Reveal>
            <Overline>The application</Overline>
            <h2 className="mt-6 font-display text-4xl font-black tracking-tighter sm:text-5xl lg:text-6xl">
              Book your onboarding call.{" "}
              <span className="text-signal">Before someone else does.</span>
            </h2>
            <p className="mt-8 max-w-lg font-body text-base leading-relaxed text-ink/60">
              This is a real conversation with our team — we'll look at your
              goals, confirm the fit, and map your six months. Ten seats, for
              our tenth anniversary. When they're claimed, the offer closes.
            </p>
            <div className="mt-10">
              <p
                data-testid="booking-qualification"
                className="max-w-lg border-l-2 border-signal pl-4 font-body text-xs leading-relaxed text-ink/60"
              >
                Before you apply: this program requires at least{" "}
                <span className="font-semibold text-ink">
                  $5,000 in working capital
                </span>{" "}
                for inventory, separate from the program fee. Without deployable
                capital the program cannot be completed.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-6">
                <a
                  href={APPLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="final-booking-cta"
                  className="group inline-flex items-center gap-3 bg-signal px-8 py-4 font-mono2 text-sm font-medium uppercase tracking-[0.15em] text-white transition-colors duration-200 hover:bg-ink hover:text-white"
                >
                  Apply — $6,997 One-Time
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </a>
                <div className="flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-ink/50">
                  <Clock className="h-4 w-4 text-signal" />
                  10 seats · 10th anniversary
                </div>
                <a
                  href={PAYMENT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="booking-payment-cta"
                  className="group inline-flex items-center gap-3 border border-ink/20 px-8 py-4 font-mono2 text-sm font-medium uppercase tracking-[0.15em] text-ink transition-colors duration-200 hover:border-signal hover:text-signal"
                >
                  Skip the call — Pay $6,997
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </a>
              </div>
            </div>
            <div className="mt-12 flex items-end gap-5 border-t border-black/10 pt-8">
              <span className="font-display text-xl font-bold text-ink/40 line-through decoration-signal decoration-2">
                $25,000
              </span>
              <span className="font-display text-5xl font-black tracking-tighter">
                $6,997
              </span>
              <span className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-volt">
                One-time · six months · 10 seats
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div
              data-testid="booking-calendar-embed"
              className="border border-black/10 bg-white"
            >
              <div className="flex items-center justify-between border-b border-black/10 px-5 py-3">
                <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-ink/50">
                  Live Calendly — pick your time
                </span>
                <span className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-volt">
                  <span className="h-2 w-2 animate-pulse-dot rounded-full bg-volt" />
                  Accepting applications
                </span>
              </div>
              <iframe
                src={`${APPLY_URL}?hide_gdpr_banner=1&primary_color=d93025`}
                title="DSA eCommerce — book your call on Calendly"
                data-testid="booking-iframe"
                className="h-[640px] w-full bg-white"
                loading="lazy"
              />
              <p className="border-t border-black/10 px-5 py-3 font-body text-xs text-ink/40">
                Calendar not loading?{" "}
                <a
                  href={APPLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="booking-fallback-link"
                  className="text-signal underline underline-offset-4"
                >
                  Open Calendly directly
                </a>
                .
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
